import { h } from 'preact'
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks'
import { createPortal } from 'preact/compat'
import { DriveStoreContext, DriveI18nContext, DriveUiContext } from './drive-context.js'
import { patch } from '@rails/request.js'

const MENU_MIN_WIDTH = 224
const MENU_MAX_HEIGHT = 256
const VIEWPORT_PADDING = 8

function tagsUpdateUrl(file) {
  if (file.is_drive_file === false) {
    return `/files/${file.id}/tags`
  }
  return `/drive/files/${file.id}/tags`
}

function contrastColor(hex) {
  const h = (hex || '#3498db').replace('#', '')
  if (h.length < 6) return '#333'
  const r = parseInt(h.substring(0, 2), 16)
  const g = parseInt(h.substring(2, 4), 16)
  const b = parseInt(h.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255.0
  return luminance > 0.55 ? '#333' : '#fff'
}

export function FileTagPicker({ file }) {
  const store = useContext(DriveStoreContext)
  const i18n  = useContext(DriveI18nContext)
  const ui    = useContext(DriveUiContext)
  const t     = (key) => (i18n ? i18n.t(key) : key)

  const [open, setOpen] = useState(false)
  const [allTags, setAllTags] = useState([])
  const [selectedIds, setSelectedIds] = useState(() => new Set((file.tags || []).map(tag => tag.id)))
  const [saving, setSaving] = useState(false)
  const [menuStyle, setMenuStyle] = useState(null)
  const rootRef = useRef(null)
  const buttonRef = useRef(null)
  const menuRef = useRef(null)

  useEffect(() => {
    setSelectedIds(new Set((file.tags || []).map(tag => tag.id)))
  }, [file.id, file.tags])

  useEffect(() => {
    if (!open || allTags.length > 0) return

    fetch('/tags.json?context=file', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(response => response.ok ? response.json() : [])
      .then(data => setAllTags(Array.isArray(data) ? data : []))
      .catch(() => setAllTags([]))
  }, [open, allTags.length])

  function buildMenuStyle(estimatedHeight = 96) {
    const button = buttonRef.current
    if (!button) return null

    const rect = button.getBoundingClientRect()
    const menuHeight = menuRef.current?.offsetHeight || estimatedHeight
    const menuWidth = menuRef.current?.offsetWidth || MENU_MIN_WIDTH
    const gap = 4
    const spaceBelow = window.innerHeight - rect.bottom - gap
    const spaceAbove = rect.top - gap
    const openUp = spaceBelow < menuHeight && spaceAbove > spaceBelow

    let top = openUp ? rect.top - menuHeight - gap : rect.bottom + gap
    let left = rect.left

    left = Math.max(VIEWPORT_PADDING, Math.min(left, window.innerWidth - menuWidth - VIEWPORT_PADDING))
    top = Math.max(VIEWPORT_PADDING, Math.min(top, window.innerHeight - menuHeight - VIEWPORT_PADDING))

    return {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      minWidth: `${MENU_MIN_WIDTH}px`,
      maxHeight: `${MENU_MAX_HEIGHT}px`,
      overflowY: 'auto',
      zIndex: 1060,
    }
  }

  function updateMenuPosition() {
    const nextStyle = buildMenuStyle()
    if (nextStyle) setMenuStyle(nextStyle)
  }

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null)
      return undefined
    }

    updateMenuPosition()
    const frameId = requestAnimationFrame(updateMenuPosition)

    function onReposition() {
      updateMenuPosition()
    }

    window.addEventListener('resize', onReposition)
    window.addEventListener('scroll', onReposition, true)
    return () => {
      cancelAnimationFrame(frameId)
      window.removeEventListener('resize', onReposition)
      window.removeEventListener('scroll', onReposition, true)
    }
  }, [open, allTags.length])

  useEffect(() => {
    if (!open) return undefined

    function onDocumentClick(event) {
      if (rootRef.current?.contains(event.target)) return
      if (menuRef.current?.contains(event.target)) return
      setOpen(false)
    }

    document.addEventListener('click', onDocumentClick)
    return () => document.removeEventListener('click', onDocumentClick)
  }, [open])

  if (!ui?.canTagFiles) return null

  async function persist(nextIds, previousTags) {
    setSaving(true)
    try {
      const response = await patch(tagsUpdateUrl(file), {
        body: JSON.stringify({ tag_ids: Array.from(nextIds) }),
        contentType: 'application/json',
        responseKind: 'json',
      })
      if (!response.ok) {
        store.updateFileTags(file.id, previousTags)
        setSelectedIds(new Set((previousTags || []).map(tag => tag.id)))
        return
      }

      // @rails/request.js exposes `json` as a Promise getter (not a method)
      const payload = await response.json
      const tags = payload.tags || []
      store.updateFileTags(file.id, tags)
      setSelectedIds(new Set(tags.map(tag => tag.id)))
    } catch {
      store.updateFileTags(file.id, previousTags)
      setSelectedIds(new Set((previousTags || []).map(tag => tag.id)))
    } finally {
      setSaving(false)
    }
  }

  function toggleTag(tagId) {
    const previousTags = file.tags || []
    const next = new Set(selectedIds)
    if (next.has(tagId)) next.delete(tagId)
    else next.add(tagId)
    setSelectedIds(next)

    const optimisticTags = allTags.filter(tag => next.has(tag.id))
    store.updateFileTags(file.id, optimisticTags)
    persist(next, previousTags)
  }

  const resolvedMenuStyle = open ? (menuStyle || buildMenuStyle()) : null

  const menu = open && resolvedMenuStyle ? (
    <div
      ref={menuRef}
      class="dropdown-menu show p-2 shadow drive-file-tag-menu"
      style={resolvedMenuStyle}
      onClick={e => e.stopPropagation()}
    >
      {allTags.length === 0 ? (
        <div class="small text-muted px-2 py-2 text-wrap" style="max-width: 16rem; white-space: normal;">
          <div>{t('drive.no_file_tags')}</div>
          <a
            href="/tags"
            class="btn btn-sm btn-link p-0 mt-1 text-start"
            onClick={e => e.stopPropagation()}
          >
            {t('drive.manage_file_tags_settings')}
          </a>
        </div>
      ) : (
        allTags.map(tag => (
          <label
            key={tag.id}
            class="dropdown-item d-flex align-items-center gap-2 small rounded py-1 px-2"
          >
            <input
              type="checkbox"
              class="form-check-input mt-0"
              checked={selectedIds.has(tag.id)}
              onChange={() => toggleTag(tag.id)}
            />
            <span
              class="badge rounded-pill"
              style={{
                backgroundColor: tag.color,
                color: contrastColor(tag.color),
              }}
            >
              {tag.tag}
            </span>
          </label>
        ))
      )}
    </div>
  ) : null

  return (
    <div class="dropdown d-inline-block ms-1" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        class="btn btn-sm btn-link text-muted p-0 align-baseline"
        title={t('drive.manage_tags')}
        aria-expanded={open}
        disabled={saving}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen((value) => {
            const next = !value
            if (next) setMenuStyle(buildMenuStyle())
            else setMenuStyle(null)
            return next
          })
        }}
      >
        <i class="bi bi-tag"></i>
      </button>
      {menu && createPortal(menu, document.body)}
    </div>
  )
}

export function FileTagChips({ tags = [] }) {
  if (!tags.length) return null

  return (
    <span class="d-inline-flex flex-wrap gap-1 ms-2 align-middle">
      {tags.map(tag => (
        <span
          key={tag.id}
          class="badge rounded-pill"
          style={{
            backgroundColor: tag.color,
            color: contrastColor(tag.color),
          }}
        >
          {tag.tag}
        </span>
      ))}
    </span>
  )
}
